import type { ToolResultPart, TranscriptEventRecord } from '@/api/types'

export interface TranscriptEntry {
  id: string
  ts: string
  kind:
    | 'user'
    | 'assistant'
    | 'thinking'
    | 'tool_use'
    | 'tool_result'
    | 'error'
    | 'done'
    | 'provider_fallback'
  text?: string
  command?: string
  description?: string
  toolName?: string
  toolUseId?: string
  parts?: ToolResultPart[]
  isError?: boolean
  truncated?: boolean
  cost?: number | null
  stopReason?: string | null
  fromModel?: string
  toModel?: string
  reason?: string
}

export interface LiveFeedEntry {
  id: string
  ts: string
  kind: string
  title: string
  summary: string
  detail?: string
  tone: 'accent' | 'warn' | 'danger' | 'info' | 'dim' | 'fg'
}

const TERMINAL_AUDIT_KINDS = new Set(['result', 'error'])

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function normalizeToolResultPart(value: unknown): ToolResultPart[] {
  if (typeof value === 'string') return [{ type: 'text', text: value }]
  if (Array.isArray(value)) return value.flatMap((item) => normalizeToolResultPart(item))

  const rec = asRecord(value)
  if (rec) {
    if (rec.type === 'text' && typeof rec.text === 'string') {
      return [{ type: 'text', text: rec.text }]
    }
    if (
      rec.type === 'image' &&
      typeof rec.mediaType === 'string' &&
      typeof rec.data === 'string'
    ) {
      return [{ type: 'image', mediaType: rec.mediaType, data: rec.data }]
    }
    if (rec.type === 'image') {
      const source = asRecord(rec.source)
      if (
        source?.type === 'base64' &&
        typeof source.media_type === 'string' &&
        typeof source.data === 'string'
      ) {
        return [{ type: 'image', mediaType: source.media_type, data: source.data }]
      }
    }
  }

  return [{ type: 'text', text: stringifyAuditValue(value) }]
}

export function normalizeToolResultParts(value: unknown): ToolResultPart[] {
  return normalizeToolResultPart(value)
}

export function summarizeToolResultParts(parts: ToolResultPart[]): string {
  return parts
    .map((part) =>
      part.type === 'text' ? part.text : `[image: ${part.mediaType || 'unknown'}]`,
    )
    .filter(Boolean)
    .join('\n')
}

export function stringifyAuditValue(value: unknown): string {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    return value
      .map((item) => stringifyAuditValue(item))
      .filter(Boolean)
      .join('\n')
  }
  const rec = asRecord(value)
  if (rec) {
    if (typeof rec.text === 'string') return rec.text
    if (typeof rec.content === 'string') return rec.content
    try {
      return JSON.stringify(value, null, 2)
    } catch {
      return String(value)
    }
  }
  if (value === null || value === undefined) return ''
  return String(value)
}

function clipLine(text: string, limit = 140): string {
  const compact = text.replace(/\s+/g, ' ').trim()
  if (!compact) return '(empty)'
  return compact.length > limit ? compact.slice(0, limit - 1) + '…' : compact
}

function toolDisplayName(name?: string): string {
  if (!name) return 'tool'
  const match = name.match(/^mcp__([^_]+(?:-[^_]+)*)__([^]+)$/)
  return match ? `${match[1]}:${match[2]}` : name
}

function summarizeToolInput(input: Record<string, unknown> | null): string {
  if (!input) return ''
  for (const key of ['command', 'description', 'url', 'query', 'text', 'key', 'uid', 'pageId', 'filePath']) {
    const value = input[key]
    if (value !== undefined && value !== null && value !== '') {
      return stringifyAuditValue(value)
    }
  }
  if (input.elements) return stringifyAuditValue(input.elements)
  return stringifyAuditValue(input)
}

function withDetail(
  title: string,
  summarySource: string,
  detailSource?: string,
  tone: LiveFeedEntry['tone'] = 'fg',
): Omit<LiveFeedEntry, 'id' | 'ts' | 'kind'> {
  const summary = clipLine(summarySource)
  const detail = (detailSource ?? summarySource).trim()
  return {
    title,
    summary,
    detail: detail && detail !== summary ? detail : undefined,
    tone,
  }
}

export function isTerminalEvents(events: TranscriptEventRecord[]): boolean {
  const last = events[events.length - 1]
  return !!last && TERMINAL_AUDIT_KINDS.has(last.kind)
}

export function eventsToTranscript(events: TranscriptEventRecord[]): TranscriptEntry[] {
  const out: TranscriptEntry[] = []
  events.forEach((e, idx) => {
    const id = `${e.ts}-${idx}`
    const p = e.payload as Record<string, unknown>
    switch (e.kind) {
      case 'user_message':
        out.push({ id, ts: e.ts, kind: 'user', text: String(p.content ?? '') })
        break
      case 'assistant_text': {
        const last = out[out.length - 1]
        const text = String(p.text ?? '')
        if (last && last.kind === 'assistant') {
          last.text = (last.text || '') + text
        } else {
          out.push({ id, ts: e.ts, kind: 'assistant', text })
        }
        break
      }
      case 'thinking':
        out.push({ id, ts: e.ts, kind: 'thinking', text: String(p.thinking ?? '') })
        break
      case 'tool_use': {
        const input = asRecord(p.input)
        out.push({
          id,
          ts: e.ts,
          kind: 'tool_use',
          command: summarizeToolInput(input),
          description: typeof input?.description === 'string' ? input.description : undefined,
          toolName: p.name as string | undefined,
        })
        break
      }
      case 'tool_result':
        {
          const parts = normalizeToolResultParts(p.content)
          const text = asString(p.text) ?? summarizeToolResultParts(parts)
          out.push({
            id,
            ts: e.ts,
            kind: 'tool_result',
            text,
            parts,
            isError: Boolean(p.is_error),
            toolUseId: p.tool_use_id as string | undefined,
            truncated: Boolean(p.truncated),
          })
        }
        break
      case 'error':
        out.push({ id, ts: e.ts, kind: 'error', text: String(p.message ?? '') })
        break
      case 'provider_fallback':
        out.push({
          id,
          ts: e.ts,
          kind: 'provider_fallback',
          fromModel: p.from_model as string | undefined,
          toModel: p.to_model as string | undefined,
          reason: p.reason as string | undefined,
        })
        break
      case 'result':
        out.push({
          id,
          ts: e.ts,
          kind: 'done',
          cost: (p.cost_usd as number | null | undefined) ?? null,
          stopReason: (p.stop_reason as string | null | undefined) ?? null,
        })
        break
      // skip: system, task_*, etc.
    }
  })
  return out
}

export function findActiveModelFromEvents(
  events: TranscriptEventRecord[],
  primary: string,
): string {
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i].kind === 'provider_fallback') {
      const to = (events[i].payload as Record<string, unknown>).to_model
      if (typeof to === 'string' && to) return to
    }
  }
  return primary
}

export function eventsToLiveFeed(events: TranscriptEventRecord[]): LiveFeedEntry[] {
  return events.map((e, idx) => {
    const id = `${e.ts}-${idx}`
    const p = e.payload as Record<string, unknown>
    const base = {
      id,
      ts: e.ts,
      kind: e.kind,
    }

    switch (e.kind) {
      case 'turn_started':
        return {
          ...base,
          ...withDetail('turn', `started · ${stringifyAuditValue(p.content)}`, undefined, 'accent'),
        }
      case 'user_message':
        return {
          ...base,
          ...withDetail('user', stringifyAuditValue(p.content), undefined, 'fg'),
        }
      case 'assistant_text':
        return {
          ...base,
          ...withDetail('assistant', stringifyAuditValue(p.text), undefined, 'accent'),
        }
      case 'thinking':
        return {
          ...base,
          ...withDetail('thinking', stringifyAuditValue(p.thinking), undefined, 'dim'),
        }
      case 'phase_changed': {
        const activity = typeof p.activity === 'string' ? p.activity : ''
        const phase = typeof p.phase === 'string' ? p.phase : 'unknown'
        return {
          ...base,
          ...withDetail('phase', activity || phase, `phase: ${phase}${activity ? `\nactivity: ${activity}` : ''}`, 'info'),
        }
      }
      case 'tool_call_started': {
        const payload = asRecord(p.payload)
        const command = summarizeToolInput(payload)
        return {
          ...base,
          ...withDetail(
            toolDisplayName(String(p.tool_name ?? 'tool')),
            `start · ${command || 'running tool'}`,
            payload ? stringifyAuditValue(payload) : undefined,
            'accent',
          ),
        }
      }
      case 'tool_use': {
        const input = asRecord(p.input)
        const command = summarizeToolInput(input)
        return {
          ...base,
          ...withDetail(
            toolDisplayName(String(p.name ?? 'tool')),
            command || 'tool requested',
            input ? stringifyAuditValue(input) : undefined,
            'accent',
          ),
        }
      }
      case 'tool_call_finished': {
        const result = asRecord(p.result)
        return {
          ...base,
          ...withDetail(
            toolDisplayName(String(p.tool_name ?? 'tool')),
            `finished · ${clipLine(stringifyAuditValue(result ?? p.result))}`,
            result ? stringifyAuditValue(result) : stringifyAuditValue(p.result),
            'info',
          ),
        }
      }
      case 'tool_result':
        {
          const parts = normalizeToolResultParts(p.content)
          const text = asString(p.text) ?? summarizeToolResultParts(parts)
          return {
            ...base,
            ...withDetail('tool result', text, text, Boolean(p.is_error) ? 'danger' : 'info'),
          }
        }
      case 'provider_fallback':
        return {
          ...base,
          ...withDetail(
            'fallback',
            `${stringifyAuditValue(p.from_model)} -> ${stringifyAuditValue(p.to_model)}`,
            stringifyAuditValue(p.reason),
            'warn',
          ),
        }
      case 'result':
        return {
          ...base,
          ...withDetail(
            'done',
            `${stringifyAuditValue(p.stop_reason || 'done')} · $${p.cost_usd ?? '?'}`,
            stringifyAuditValue(p.usage),
            'accent',
          ),
        }
      case 'error':
        return {
          ...base,
          ...withDetail(
            'error',
            stringifyAuditValue(p.message),
            stringifyAuditValue(p.diagnostics),
            'danger',
          ),
        }
      case 'session_title_updated':
        return {
          ...base,
          ...withDetail('title', `updated · ${stringifyAuditValue(p.title)}`, undefined, 'info'),
        }
      case 'admin_force_stop':
        return {
          ...base,
          ...withDetail(
            'admin',
            `force stop · ${stringifyAuditValue(p.admin_email)}`,
            stringifyAuditValue(p),
            'danger',
          ),
        }
      case 'stall_detected':
        return {
          ...base,
          ...withDetail('watchdog', stringifyAuditValue(p.reason ?? 'stall detected'), stringifyAuditValue(p), 'danger'),
        }
      case 'probe_result':
        return {
          ...base,
          ...withDetail('probe', stringifyAuditValue(p.summary ?? p.error ?? p.host ?? 'probe result'), stringifyAuditValue(p), 'info'),
        }
      case 'rate_limit':
        return {
          ...base,
          ...withDetail('rate limit', stringifyAuditValue(p.info), undefined, 'warn'),
        }
      case 'stream_event':
        return {
          ...base,
          ...withDetail('stream', stringifyAuditValue(p.event), stringifyAuditValue(p), 'info'),
        }
      case 'system': {
        const subtype = typeof p.subtype === 'string' ? p.subtype : 'system'
        const data = asRecord(p.data)
        if (subtype === 'api_retry' && data) {
          const attempt = stringifyAuditValue(data.attempt)
          const maxRetries = stringifyAuditValue(data.max_retries)
          const error = stringifyAuditValue(data.error || data.error_status)
          return {
            ...base,
            ...withDetail(
              'retry',
              `attempt ${attempt}/${maxRetries} · ${error}`,
              stringifyAuditValue(data),
              'warn',
            ),
          }
        }
        if (subtype === 'init' && data) {
          return {
            ...base,
            ...withDetail(
              'init',
              `model ${stringifyAuditValue(data.model)} · session ready`,
              stringifyAuditValue(data),
              'info',
            ),
          }
        }
        return {
          ...base,
          ...withDetail(subtype, stringifyAuditValue(data ?? p), stringifyAuditValue(data ?? p), 'info'),
        }
      }
      default:
        return {
          ...base,
          ...withDetail(e.kind.replace(/_/g, ' '), stringifyAuditValue(p), stringifyAuditValue(p), 'dim'),
        }
    }
  })
}

/**
 * Group a flat entry list into per-turn arrays. A turn starts at every
 * `user` entry and runs until the next `user`. Entries that appear before
 * the first `user` (e.g. leading `session` noise after reattach) are folded
 * into the first turn.
 */
export function groupTurns(entries: TranscriptEntry[]): TranscriptEntry[][] {
  const out: TranscriptEntry[][] = []
  for (const e of entries) {
    if (e.kind === 'user') {
      out.push([e])
    } else if (out.length === 0) {
      out.push([e])
    } else {
      out[out.length - 1].push(e)
    }
  }
  return out
}

/** Whether a turn has emitted its terminal entry (`done` or `error`). */
export function isTurnComplete(turn: TranscriptEntry[]): boolean {
  return turn.some((e) => e.kind === 'done' || e.kind === 'error')
}

/** Stable key for a turn: id of its first entry. */
export function turnKey(turn: TranscriptEntry[]): string {
  return turn[0]?.id ?? 'empty'
}
